import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { revealPendingScan } from '../../services/api/abhaApi';
import ScanShareQueueSync from './ScanShareQueueSync';
import useScanQueueStore from '../../stores/scanQueueStore';
import useScanCounters from '../../hooks/useScanCounters';
import { usePermissions } from '../../context/PermissionContext';
import useAdmissionEnabled from '../../hooks/useAdmissionEnabled';
import { ADMISSION_PERMISSIONS } from '../../constants/admission';
import { ROUTES } from '../../constants/routes';
import { scanSnapshot } from '../../utils/scanShare';
import { counterBadge, typeBadgeClass } from '../../utils/scanCounters';
import { SCAN_SHARE_COPY, REGISTER_OPD, ADMIT_IPD } from '../../constants/abdmCopy';

export const SCAN_FILTER_KEY = 'ak_scan_share_counter';
const ALL = 'ALL';

const readFilter = () => { try { return localStorage.getItem(SCAN_FILTER_KEY) || ALL; } catch (e) { return ALL; } };
const writeFilter = (id) => { try { localStorage.setItem(SCAN_FILTER_KEY, id); } catch (e) { /* private mode: not remembered */ } };

const minutesFrom = (iso) => Math.round((new Date(iso).getTime() - Date.now()) / 60000);

function agoLabel(iso) {
  const m = Math.max(0, -minutesFrom(iso));
  return m < 1 ? 'just now' : `${m} min ago`;
}

function expiryLabel(iso) {
  const m = minutesFrom(iso);
  return m <= 0 ? 'expiring now' : `expires in ${m} min`;
}

const updatedLabel = (ms) => {
  const m = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  return m < 1 ? 'Updated just now' : `Updated ${m} min ago`;
};

/**
 * Desk-wide Scan & Share waiting list (R56/R150) - a view of the shared queue store; it mounts the one
 * poller (ScanShareQueueSync) itself, so it can be dropped into any host page. Newest first,
 * bounded (server caps at 50), scrollable, filterable by counter (choice remembered per device).
 *
 * Picking an entry reveals the unmasked profile (explicit staff action = the human match) and hands
 * it plus the scan_id to the OPD form or the admission form, which claim it once the patient exists.
 * `onRegisterOpd` / `onAdmit` let a host that already shows the target form take the snapshot
 * in place; otherwise the panel navigates there.
 */
export default function ScanShareQueuePanel(props) {
  const { hasPermission } = usePermissions();
  const allowed = hasPermission('ABHA_SCAN_SHARE');
  // Without ABHA_SCAN_SHARE the queue API refuses (403): mount nothing - no fetch, no counters call.
  if (!allowed) return null;
  return <QueuePanel {...props} />;
}

function QueuePanel({ onRegisterOpd, onAdmit }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canOpd = hasPermission('CREATE_VISIT');
  const admissionOn = useAdmissionEnabled();
  const canAdmit = hasPermission(ADMISSION_PERMISSIONS.ADMIT_PATIENT) && admissionOn;

  const entries = useScanQueueStore((s) => s.entries);
  const updatedAt = useScanQueueStore((s) => s.updatedAt);
  const loadError = useScanQueueStore((s) => s.loadError);
  const load = useScanQueueStore((s) => s.load);
  const { counters } = useScanCounters(true);

  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(readFilter);
  // A remembered counter that no longer exists falls back to All (without erasing the memory).
  const filter = selected !== ALL && counters.some((c) => c.counterId === selected) ? selected : ALL;

  const choose = (id) => { setSelected(id); writeFilter(id); };
  const refresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  const pick = async (entry, target) => {
    setBusyId(entry.scanId);
    setMsg('');
    try {
      const full = await revealPendingScan({ scanId: entry.scanId });
      if (!full) {
        setMsg('This scan is no longer waiting. The list has been refreshed.');
        load();
        return;
      }
      const snapshot = scanSnapshot(full, entry.scanId, entry.abhaMasked);
      if (target === 'opd') {
        if (onRegisterOpd) onRegisterOpd(snapshot);
        else navigate(ROUTES.ORG, { state: { openRegister: true, abhaSnapshot: snapshot } });
      } else if (onAdmit) onAdmit(snapshot);
      else navigate(ROUTES.ADMIT_PATIENT, { state: { abhaSnapshot: snapshot } });
    } catch (e) {
      setMsg(`Could not open this scan: ${e?.message || 'please try again.'}`);
    } finally {
      setBusyId(null);
    }
  };

  const visible = entries && (filter === ALL ? entries : entries.filter((e) => e.counterId === filter));
  const otherCount = entries && visible ? entries.length - visible.length : 0;
  const filterName = counters.find((c) => c.counterId === filter)?.name || '';

  const btn = 'px-3 py-1 rounded-full text-[11px] font-semibold disabled:opacity-60';
  const filled = 'bg-teal-600 text-white border border-teal-600';
  const outline = 'border border-teal-600 dark:border-teal-500 bg-white dark:bg-slate-800 text-teal-800 dark:text-teal-200';
  const chip = (active) => `px-3 py-1 rounded-full text-[11px] font-semibold border border-teal-600 dark:border-teal-500 ${active ? 'bg-teal-600 text-white' : 'bg-white dark:bg-slate-800 text-teal-800 dark:text-teal-200'}`;

  return (
    <section
      data-testid="scan-share-queue"
      className="mb-4 rounded-xl border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/40 p-3"
    >
      <ScanShareQueueSync />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
          Scan &amp; Share waiting{entries && entries.length > 0 ? ` (${entries.length})` : ''}
        </h2>
        <span className="flex items-center gap-2">
          {updatedAt && <span className="text-[11px] text-slate-600 dark:text-slate-300" data-testid="scan-share-updated">{updatedLabel(updatedAt)}</span>}
          <button
            type="button"
            data-testid="scan-share-refresh"
            disabled={refreshing}
            onClick={refresh}
            className="px-3 py-1 rounded-md border border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[11px] font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </span>
      </div>
      {counters.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="scan-share-chips">
          <button type="button" data-testid="scan-share-chip" className={chip(filter === ALL)} onClick={() => choose(ALL)}>All</button>
          {counters.map((c) => (
            <button key={c.counterId} type="button" data-testid="scan-share-chip" className={chip(filter === c.counterId)} onClick={() => choose(c.counterId)}>{c.name}</button>
          ))}
        </div>
      )}
      {msg && <p className="mt-1 text-[12px] text-red-700 dark:text-red-300" data-testid="scan-share-queue-msg">{msg}</p>}
      {loadError && !entries && (
        <p className="mt-2 text-[12.5px] text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/40 border border-amber-500 rounded-lg p-2" data-testid="scan-share-queue-error">
          Could not load the waiting list. <button type="button" onClick={refresh} className="underline font-semibold">Try again</button>
        </p>
      )}
      {loadError && entries && <p className="mt-1 text-[12px] text-amber-800 dark:text-amber-300" data-testid="scan-share-queue-error">Could not refresh the Scan &amp; Share list. Retrying shortly.</p>}
      {entries && entries.length === 0 && (
        <p className="mt-1 text-[12.5px] text-slate-700 dark:text-slate-300" data-testid="scan-share-queue-empty">
          No patients are waiting. When a patient scans a counter QR, they appear here.
        </p>
      )}
      {entries && entries.length > 0 && visible.length === 0 && (
        <p className="mt-1 text-[12.5px] text-slate-700 dark:text-slate-300" data-testid="scan-share-filtered-empty">
          No one is waiting at {filterName}. {otherCount} {otherCount === 1 ? 'is' : 'are'} waiting at other counters - choose &quot;All&quot; to see them.
        </p>
      )}
      {visible && visible.length > 0 && (
        <ul className="mt-2 max-h-72 overflow-y-auto divide-y divide-teal-200 dark:divide-teal-800">
          {visible.map((e) => (
            <li key={e.scanId} data-testid="scan-share-entry" className="flex flex-wrap items-center gap-3 py-2">
              <span
                data-testid="scan-share-token"
                className="shrink-0 min-w-[3.5rem] text-center text-lg font-bold text-teal-800 dark:text-teal-200"
              >
                {e.tokenNumber != null ? SCAN_SHARE_COPY.token(e.tokenNumber) : '—'}
              </span>
              <span className="flex-1 min-w-[10rem] text-[12.5px] text-slate-800 dark:text-slate-100">
                <span className="block font-medium">{e.nameMasked || '—'}</span>
                <span className="block text-[12px] text-slate-700 dark:text-slate-300">
                  {[e.gender, e.yearOfBirth && `b. ${e.yearOfBirth}`, e.mobileMasked].filter(Boolean).join(' · ')}
                </span>
                {e.counterId && (
                  <span data-testid="scan-share-counter" className={`my-0.5 inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${typeBadgeClass(e.counterType)}`}>
                    {counterBadge(e.counterId, e.counterType)}
                  </span>
                )}
                <span className="block text-[11px] text-slate-600 dark:text-slate-400">
                  Scanned {agoLabel(e.scannedAt)} · {expiryLabel(e.expiresAt)}
                </span>
              </span>
              <span className="flex gap-2">
                {canOpd && (
                  <button
                    type="button"
                    data-testid="scan-share-opd"
                    disabled={busyId === e.scanId}
                    onClick={() => pick(e, 'opd')}
                    className={`${btn} ${e.counterType === 'OPD' ? filled : outline}`}
                  >
                    {REGISTER_OPD}
                  </button>
                )}
                {canAdmit && (
                  <button
                    type="button"
                    data-testid="scan-share-admit"
                    disabled={busyId === e.scanId}
                    onClick={() => pick(e, 'ipd')}
                    className={`${btn} ${e.counterType === 'IPD' ? filled : outline}`}
                  >
                    {ADMIT_IPD}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {entries && entries.length >= 50 && (
        <p className="mt-2 text-center text-[11px] text-slate-600 dark:text-slate-400">Showing newest 50 · scroll inside the list</p>
      )}
    </section>
  );
}
